import { REGISTRY_BLOCK, THEME_ID, type RegistryBlockType, type ThemeId } from '../catalog/platformCatalogIds';

const T = REGISTRY_BLOCK.type;

/**
 * Reserved mid-page header align hint on the composition recipe.
 * **Not applied in CSS** — block arrangements own title/subtitle alignment
 * in `site.css`. A former `data-section-align` chrome fought designed
 * mastheads (Editorial Board, Highlight Strip, Course Timeline, …).
 */
export const SECTION_ALIGN = {
  start: 'start',
  center: 'center',
} as const;
export type SectionAlign = (typeof SECTION_ALIGN)[keyof typeof SECTION_ALIGN];

/**
 * How the hero heading relates to the nav wordmark.
 * Seeds respect this so brand is not restated in the first viewport.
 */
export const BRAND_IN_HERO = {
  /** Atmosphere / welcome line only — no business name in H1. */
  omit: 'omit',
  /** Short welcome; brand lives in nav. */
  soft: 'soft',
  /** Legacy `{business} — Welcome` pattern. */
  full: 'full',
} as const;
export type BrandInHero = (typeof BRAND_IN_HERO)[keyof typeof BRAND_IN_HERO];

/** Dark-surface recipes after luxury normalize. Light themes use `none`. */
export const ATMOSPHERE = {
  none: 'none',
  inkDeep: 'ink-deep',
  velvetRoom: 'velvet-room',
  neonVoid: 'neon-void',
  streetAsphalt: 'street-asphalt',
} as const;
export type AtmosphereId = (typeof ATMOSPHERE)[keyof typeof ATMOSPHERE];

export interface ThemeSiteComposition {
  /** Unused by render — kept for recipe completeness; see `SECTION_ALIGN`. */
  sectionAlign: SectionAlign;
  brandInHero: BrandInHero;
  /**
   * Matrix `optionalAddonBlocks` (or other entitled types) to auto-insert
   * for this theme at bootstrap. Must still be in tier `allowedBlocks`.
   */
  homeIncludeOptional: readonly RegistryBlockType[];
  /**
   * Tier-entitled types to **omit** from Home default starters for this theme
   * (e.g. Live GPS when Stop already owns the map). Still available to add later.
   */
  homeExcludeBlocks?: readonly RegistryBlockType[];
  /**
   * Mid-page blocks seeded on `/about-us` after the about hero.
   * When `/about-us` exists, these types are **owned by About** and stripped
   * from the home canvas so the same block never appears on both pages.
   * Still filtered by tier `allowedBlocks` at bootstrap.
   */
  aboutIncludeBlocks: readonly RegistryBlockType[];
  /**
   * When true, `/about-us` seeds no story hero — mid-page blocks open the page
   * (e.g. Kitchen Team then philosophy). Home still has its own hero.
   */
  aboutOmitHero?: boolean;
  /**
   * Mid-page blocks seeded on `/menu` after the Speisekarte.
   * Owned by Menu and stripped from Home when `/menu` exists.
   */
  menuIncludeBlocks?: readonly RegistryBlockType[];
  /**
   * Blocks seeded on `/contact` after the Contact arrangement.
   * Slow inquiries (buyout, chef’s table) — not the evening-table form on Home.
   */
  contactIncludeBlocks?: readonly RegistryBlockType[];
  /**
   * Home canvas order after ownership splits. Listed types sort first;
   * anything else keeps its relative order at the end. Highlight menus
   * count as `staticMenu`.
   */
  homeCanvasOrder?: readonly RegistryBlockType[];
  atmosphere: AtmosphereId;
}

/**
 * Page-level recipes for all gastronomy themes.
 * Arrangement picks stay in `THEME_BLOCK_PRESENTATIONS`.
 */
export const THEME_SITE_COMPOSITION: Record<ThemeId, ThemeSiteComposition> = {
  [THEME_ID.michelinDark]: {
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.soft,
    /**
     * Landing rhythm: plate → short list → the room → pairing (Plus) → table.
     * The room ledger is what a guest needs before they book; the kitchen
     * story stays on About. Private dining is a slow inquiry on Kontakt.
     */
    homeIncludeOptional: [T.imageGallery, T.tastingTimeline, T.reservation],
    homeCanvasOrder: [
      T.hero,
      T.staticMenu,
      T.imageGallery,
      T.tastingTimeline,
      T.reservation,
    ],
    /**
     * Team opens About (no tall Quiet Luxury hero). Philosophy follows as Notes.
     * A buyout form does not follow the kitchen story.
     */
    aboutOmitHero: true,
    aboutIncludeBlocks: [T.kitchenTeam, T.chefStory],
    /**
     * Address and a house note first. The closed-room inquiry follows as its
     * own chapter (date and covers — not a second copy of the message form).
     * Evening tables stay on Home.
     */
    contactIncludeBlocks: [T.privatePartyInquiry],
    /** Weinkarte sits with Speisekarte — not a bar tap list on Home. */
    menuIncludeBlocks: [T.drinksList],
    atmosphere: ATMOSPHERE.inkDeep,
  },
  [THEME_ID.limestoneOrangery]: {
    sectionAlign: SECTION_ALIGN.center,
    brandInHero: BRAND_IN_HERO.soft,
    homeIncludeOptional: [T.reservation, T.tastingTimeline],
    aboutIncludeBlocks: [T.chefStory, T.imageGallery, T.privatePartyInquiry],
    menuIncludeBlocks: [T.drinksList],
    atmosphere: ATMOSPHERE.none,
  },
  [THEME_ID.moorlandHearth]: {
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.omit,
    homeIncludeOptional: [T.reservation, T.tastingTimeline],
    aboutIncludeBlocks: [T.chefStory, T.imageGallery, T.privatePartyInquiry],
    menuIncludeBlocks: [T.drinksList],
    atmosphere: ATMOSPHERE.none,
  },
  [THEME_ID.bistroBrasserie]: {
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.soft,
    /** Family excludes tasting / private party — Home stays reserve-led at every tier. */
    homeIncludeOptional: [T.reservation],
    aboutIncludeBlocks: [T.chefStory, T.imageGallery],
    atmosphere: ATMOSPHERE.none,
  },
  [THEME_ID.hinokiCounter]: {
    /**
     * Counter film (Recipe B): Counter Bleed → Chef’s Cut → Omakase (Plus) →
     * Messenger sitting strip. Filmstrip lives on About under Kitchen Team
     * (chef quote → team → gallery) — not a Home strip. Colophon still centers
     * its own seal.
     */
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.omit,
    homeIncludeOptional: [T.reservation, T.tastingTimeline],
    homeCanvasOrder: [
      T.hero,
      T.staticMenu,
      T.tastingTimeline,
      T.reservation,
    ],
    aboutIncludeBlocks: [T.chefStory, T.kitchenTeam, T.imageGallery],
    atmosphere: ATMOSPHERE.none,
  },
  [THEME_ID.lotusSteam]: {
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.soft,
    /**
     * Landing: bowl highlight → herb-mist filmstrip → table (tasting at Plus).
     * Kitchen people stay on About under the chef strip — not a Home roster.
     */
    homeIncludeOptional: [T.imageGallery, T.reservation, T.tastingTimeline],
    homeCanvasOrder: [
      T.hero,
      T.staticMenu,
      T.imageGallery,
      T.tastingTimeline,
      T.reservation,
    ],
    aboutIncludeBlocks: [T.chefStory, T.kitchenTeam],
    atmosphere: ATMOSPHERE.none,
  },
  [THEME_ID.tileAwning]: {
    /**
     * Start rail with the full-bleed hero stack — not center mid-page chrome.
     * Price-tile Beliebt is a market board (left intro); center fought that + hero.
     */
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.omit,
    /**
     * Shopfront landing: price-tile Beliebt → mosaic → reserve (no tasting strip).
     * Kitchen people stay on About under the chef quote.
     */
    homeIncludeOptional: [T.imageGallery, T.reservation],
    homeCanvasOrder: [T.hero, T.staticMenu, T.imageGallery, T.reservation],
    aboutIncludeBlocks: [T.chefStory, T.kitchenTeam],
    atmosphere: ATMOSPHERE.none,
  },
  [THEME_ID.lanternCourt]: {
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.omit,
    /**
     * Izakaya landing: today-split → masonry → course board (Plus) → reserve.
     * Kitchen people stay on About under the chef story.
     */
    homeIncludeOptional: [T.imageGallery, T.reservation, T.tastingTimeline],
    homeCanvasOrder: [
      T.hero,
      T.staticMenu,
      T.imageGallery,
      T.tastingTimeline,
      T.reservation,
    ],
    aboutIncludeBlocks: [T.chefStory, T.kitchenTeam],
    atmosphere: ATMOSPHERE.none,
  },
  [THEME_ID.brutalistGrind]: {
    sectionAlign: SECTION_ALIGN.center,
    brandInHero: BRAND_IN_HERO.soft,
    homeIncludeOptional: [],
    /** Community voice on About; hours / map / counter stay on Home. */
    aboutIncludeBlocks: [T.socialFeed, T.imageGallery],
    atmosphere: ATMOSPHERE.none,
  },
  [THEME_ID.earthClay]: {
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.soft,
    homeIncludeOptional: [],
    aboutIncludeBlocks: [T.socialFeed, T.imageGallery],
    atmosphere: ATMOSPHERE.none,
  },
  [THEME_ID.neonNoir]: {
    sectionAlign: SECTION_ALIGN.center,
    brandInHero: BRAND_IN_HERO.omit,
    homeIncludeOptional: [],
    /** Events / bookings story on About; drinks + cocktail stay on Home. */
    aboutIncludeBlocks: [T.privatePartyInquiry, T.imageGallery],
    atmosphere: ATMOSPHERE.neonVoid,
  },
  [THEME_ID.velvetVintage]: {
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.soft,
    homeIncludeOptional: [],
    aboutIncludeBlocks: [T.privatePartyInquiry, T.imageGallery],
    atmosphere: ATMOSPHERE.velvetRoom,
  },
  [THEME_ID.boldStreet]: {
    sectionAlign: SECTION_ALIGN.center,
    brandInHero: BRAND_IN_HERO.soft,
    homeIncludeOptional: [],
    /** Catering story + FAQ on About; stop / route stay on Home (food_truck Bold Street). */
    aboutIncludeBlocks: [T.cateringInquiry, T.faq, T.imageGallery],
    atmosphere: ATMOSPHERE.streetAsphalt,
  },
  [THEME_ID.mustardRail]: {
    sectionAlign: SECTION_ALIGN.center,
    brandInHero: BRAND_IN_HERO.soft,
    homeIncludeOptional: [],
    /** Restaurant Imbiss omits About; keep a gallery recipe if About is force-enabled. */
    aboutIncludeBlocks: [T.imageGallery],
    atmosphere: ATMOSPHERE.streetAsphalt,
  },
  [THEME_ID.tahiniWindow]: {
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.soft,
    homeIncludeOptional: [],
    aboutIncludeBlocks: [T.imageGallery],
    atmosphere: ATMOSPHERE.none,
  },
  [THEME_ID.cleanNomad]: {
    sectionAlign: SECTION_ALIGN.start,
    brandInHero: BRAND_IN_HERO.soft,
    homeIncludeOptional: [],
    /** Stop board already owns map + hours — Live GPS would duplicate the dock. */
    homeExcludeBlocks: [T.liveGps],
    aboutIncludeBlocks: [T.cateringInquiry, T.faq, T.imageGallery],
    atmosphere: ATMOSPHERE.none,
  },
};

export function getThemeSiteComposition(themeId?: string): ThemeSiteComposition | undefined {
  if (!themeId) return undefined;
  return THEME_SITE_COMPOSITION[themeId as ThemeId];
}

/** HTML data-attribute helpers for `.ws-site`. */
export function compositionSiteDataAttributes(themeId?: string): {
  'data-brand-in-hero'?: BrandInHero;
  'data-atmosphere'?: AtmosphereId;
} {
  const recipe = getThemeSiteComposition(themeId);
  if (!recipe) return {};
  return {
    'data-brand-in-hero': recipe.brandInHero,
    'data-atmosphere': recipe.atmosphere,
  };
}
